'use client';

import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTpSlOrders } from '@/lib/api/order/hooks';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import {
  useTradingBalances,
  useTradingTradeHistory,
} from '@/lib/components/Trading/TradingDataContext';
import { useCoins } from '@/lib/providers/CoinsProvider';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { useOutcomePrices } from '@/lib/store/prices-store';
import { EMarketAction } from '@/lib/types/market-action';
import { EMarketIntent } from '@/lib/types/market-intent';
import { EMarketOrderType } from '@/lib/types/market-order-type';
import { Icon } from '@/lib/ui-kit/icons';
import { InlineEmpty } from '@/lib/ui-kit/inline-empty';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';
import { DialogTakeProfitStopLoss } from './DialogTakeProfitStopLoss';
import { DialogSharePosition } from './SharePosition';

// PnL cell with pulse animation on value change
// The formatted figure can be a threshold marker ("> -$0.001"), so the sign lives
// somewhere inside the string rather than at its start.
function PnlCell({ amount, percent }: { amount: string; percent: string }) {
  const [flash, setFlash] = useState<'flash-up' | 'flash-down' | null>(null);
  const prevAmountRef = useRef(amount);

  useEffect(() => {
    if (prevAmountRef.current !== amount) {
      const prev = Number(prevAmountRef.current.replace(/[^\d.-]/g, ''));
      const curr = Number(amount.replace(/[^\d.-]/g, ''));

      if (curr > prev) {
        setFlash('flash-up');
      } else if (curr < prev) {
        setFlash('flash-down');
      }

      prevAmountRef.current = amount;

      const timer = setTimeout(() => setFlash(null), 400);
      return () => clearTimeout(timer);
    }
  }, [amount]);

  const isNegative = percent.includes('-');

  return (
    <div className="flex w-[100px] flex-col whitespace-nowrap text-right">
      <span className={cn('flash-value text-[13px] leading-[1.5] text-v2-text-primary', flash)}>
        {amount}
      </span>
      <span
        className={cn(
          'flash-value text-[13px] leading-[1.5]',
          isNegative ? 'text-red-400' : 'text-v2-green',
          flash,
        )}
      >
        {isNegative ? percent : `+${percent}`}
      </span>
    </div>
  );
}

type OpenPositionRow = {
  id: string;
  outcome: 'Yes' | 'No';
  shares: number;
  openPrice: string; // 58¢
  tpSl?: {
    ordersCount: number;
    takeProfitPrice?: number;
    stopLossPrice?: number;
    totalTpSize?: number;
    totalSlSize?: number;
  } | null; // TP/SL data: prices for single order, sizes for multiple
  value: string; // $1,101
  cost: string; // $1,000
  /** Raw figures for the share dialog — never parse them back out of the strings above. */
  costValue: number;
  openPriceCents: number;
  pnl: { amount: string; percent: string }; // $100 (10.01%)
  eventTokenId: string; // Token ID for identifying the position
  totalShares: number;
};

export const EventOpenPositionsTable = () => {
  const t = useTranslations('event.openPositions');
  const tCommon = useTranslations('common');
  const [openTpSlDialog, setOpenTpSlDialog] = useState(false);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<OpenPositionRow | null>(null);
  const [sharePosition, setSharePosition] = useState<OpenPositionRow | null>(null);
  const selectedOutcome = useSelectedOutcome();
  const { user } = useAuthContext();
  const userId = user?.id;
  const outcomeId = selectedOutcome?.id ?? '';
  const resolutions = selectedOutcome?.r; // Array of Resolution objects with id and name
  const event = useOrderFormStore.use.event();
  const { coins } = useCoins();

  // Get USDC assetId
  const quoteAssetId = useMemo(() => {
    const usdcCoin = coins.find((coin) => coin.shortName.toUpperCase() === 'USDC');
    return usdcCoin?.id || '';
  }, [coins]);

  // Get store actions for filling order form
  const setAction = useOrderFormStore.use.setAction();
  const setIntent = useOrderFormStore.use.setIntent();
  const setOrderType = useOrderFormStore.use.setOrderType();
  const setSharesCount = useOrderFormStore.use.setSharesCount();
  const setLimitPrice = useOrderFormStore.use.setLimitPrice();
  const setAmountUsd = useOrderFormStore.use.setAmountUsd();
  const setIsMobileFormOpened = useOrderFormStore.use.setIsMobileFormOpened();
  const triggerLightningAnimation = useOrderFormStore.use.triggerLightningAnimation();

  // Get YES and NO resolution IDs (eventTokenIds)
  const yesResolution = resolutions?.find((r) => r.isPositive === true);
  const noResolution = resolutions?.find((r) => r.isPositive === false);
  const yesTokenId = yesResolution?.id;
  const noTokenId = noResolution?.id;

  // Determine primary: YES resolution is primary (isPositive === true), NO is not (isPositive === false)
  const yesPrimary = yesResolution ? yesResolution.isPositive === true : undefined;
  const noPrimary = noResolution ? noResolution.isPositive === true : undefined;

  // Get TP/SL orders for YES and NO positions
  const yesTpSlOrders = useTpSlOrders(
    { assetId: yesTokenId || '', status: 'ACTIVE', primary: yesPrimary },
    { enabled: Boolean(yesTokenId) },
  );
  const noTpSlOrders = useTpSlOrders(
    { assetId: noTokenId || '', status: 'ACTIVE', primary: noPrimary },
    { enabled: Boolean(noTokenId) },
  );

  // Get user balances from context (already fetched by TradingDataProvider)
  const balancesData = useTradingBalances();

  // Get trade history from context (already fetched by TradingDataProvider)
  const tradeHistoryData = useTradingTradeHistory(outcomeId);

  // Loading state: only show loading if balances haven't loaded yet
  // Don't wait for tradeHistoryData - it's only needed for average price calculation
  // Positions can be displayed even without trade history (with 0¢ open price)
  const isLoading = !balancesData;

  // Get current prices from WebSocket
  const livePrices = useOutcomePrices(outcomeId);

  // Calculate current price for YES and NO based on order book prices
  // Note: bestAsk/bestBid of 0 means empty order book, not a valid price
  const currentYesPrice = useMemo(() => {
    if (typeof livePrices?.bestAsk === 'number' && livePrices.bestAsk > 0) {
      return livePrices.bestAsk;
    }
    if (typeof livePrices?.bestBid === 'number' && livePrices.bestBid > 0) {
      return 1 - livePrices.bestBid;
    }
    if (livePrices?.chance && livePrices.chance > 0) {
      return livePrices.chance;
    }
    return yesResolution?.price ?? 0;
  }, [livePrices, yesResolution]);

  const currentNoPrice = useMemo(() => {
    if (typeof livePrices?.bestAsk === 'number' && livePrices.bestAsk > 0) {
      return 1 - livePrices.bestAsk;
    }
    if (typeof livePrices?.bestBid === 'number' && livePrices.bestBid > 0) {
      return livePrices.bestBid;
    }
    if (livePrices?.chance && livePrices.chance > 0) {
      return 1 - livePrices.chance;
    }
    return noResolution?.price ?? 0;
  }, [livePrices, noResolution]);

  // Calculate average purchase price from trade history
  const calculateAveragePurchasePrice = useCallback(
    (assetId: string, shares: number): Decimal => {
      if (!tradeHistoryData?.content || !userId || shares < 0) {
        return new Decimal(0);
      }

      // Filter trades where user was buyer and matches the assetId
      const buyTrades = tradeHistoryData.content.filter(
        (trade) => trade.buyerId === userId && trade.assetId === assetId,
      );

      if (buyTrades.length === 0) {
        return new Decimal(0);
      }

      // Calculate weighted average price
      let totalCost = new Decimal(0);
      let totalQuantity = new Decimal(0);

      for (const trade of buyTrades) {
        const price = new Decimal(trade.price);
        const quantity = new Decimal(trade.quantity);
        totalCost = totalCost.plus(price.mul(quantity));
        totalQuantity = totalQuantity.plus(quantity);
      }

      if (totalQuantity.isZero()) {
        return new Decimal(0);
      }

      return totalCost.dividedBy(totalQuantity);
    },
    [tradeHistoryData, userId],
  );

  const rows: OpenPositionRow[] = useMemo(() => {
    // Early return if required data is missing
    if (!balancesData?.balances || balancesData.balances.length === 0 || !resolutions) {
      return [];
    }

    const positions: OpenPositionRow[] = [];

    // Process YES position
    if (yesTokenId) {
      const yesBalance = balancesData.balances.find((b) => b.eventTokenId === yesTokenId);

      const yesTotalVolume = yesBalance?.totalVolume ?? 0;
      const yesAvailableShares = yesBalance?.value ?? 0; // available = b - f

      if (yesBalance && yesTotalVolume > 0) {
        // Use totalVolume for calculations (full position), but show availableShares in table
        const totalShares = yesTotalVolume;
        // Use averageOpenPrice from API if available, otherwise calculate from trade history
        const avgPurchasePrice =
          yesBalance.averageOpenPrice !== undefined
            ? new Decimal(yesBalance.averageOpenPrice)
            : calculateAveragePurchasePrice(yesTokenId, totalShares);
        // Cost = total shares × average purchase price (what we paid)
        const cost =
          yesBalance.cost !== undefined
            ? new Decimal(yesBalance.cost)
            : avgPurchasePrice.mul(totalShares);
        // Value = current price × total shares
        const value = new Decimal(currentYesPrice).mul(totalShares);
        const pnl = value.minus(cost);
        const pnlPercent = cost.isZero()
          ? new Decimal(0)
          : value.dividedBy(cost).mul(100).minus(100);

        // Calculate TP/SL display data
        const yesTpSlData = yesTpSlOrders.data || [];
        let yesTpSlDisplay = null;
        if (yesTpSlData.length > 0) {
          if (yesTpSlData.length === 1) {
            // Single order: show prices
            yesTpSlDisplay = {
              ordersCount: 1,
              takeProfitPrice: yesTpSlData[0].takeProfitPrice,
              stopLossPrice: yesTpSlData[0].stopLossPrice,
            };
          } else {
            // Multiple orders: show total sizes
            yesTpSlDisplay = {
              ordersCount: yesTpSlData.length,
              totalTpSize: yesTpSlData.reduce(
                (sum, order) => (order.takeProfitPrice > 0 ? sum + order.quantity : sum),
                0,
              ),
              totalSlSize: yesTpSlData.reduce(
                (sum, order) => (order.stopLossPrice > 0 ? sum + order.quantity : sum),
                0,
              ),
            };
          }
        }

        positions.push({
          id: `yes-${yesTokenId}`,
          outcome: 'Yes',
          totalShares: totalShares,
          shares: yesAvailableShares, // Show available shares (b - f)
          eventTokenId: yesTokenId,
          openPrice: avgPurchasePrice.isZero() ? '0¢' : formatCents(avgPurchasePrice.mul(100)),
          tpSl: yesTpSlDisplay,
          value: formatMoney(value.toNumber(), { prefix: '$' }),
          cost: formatMoney(cost.toNumber(), { prefix: '$' }),
          costValue: cost.toNumber(),
          openPriceCents: avgPurchasePrice.mul(100).toNumber(),
          pnl: {
            amount: formatMoney(pnl.toNumber(), { prefix: '$' }),
            percent: `${formatMoney(pnlPercent.toNumber(), { decimals: 2 })}%`,
          },
        });
      }
    }

    // Process NO position
    if (noTokenId) {
      const noBalance = balancesData.balances.find((b) => b.eventTokenId === noTokenId);
      // Show position if totalVolume > 0 (total shares, not affected by frozen)
      const noTotalVolume = noBalance?.totalVolume ?? 0;
      const noAvailableShares = noBalance?.value ?? 0; // available = b - f

      if (noBalance && noTotalVolume > 0) {
        // Use totalVolume for calculations (full position), but show availableShares in table
        const totalShares = noTotalVolume;
        // Use averageOpenPrice from API if available, otherwise calculate from trade history
        const avgPurchasePrice =
          noBalance.averageOpenPrice !== undefined
            ? new Decimal(noBalance.averageOpenPrice)
            : calculateAveragePurchasePrice(noTokenId, totalShares);
        // Cost = total shares × average purchase price (what we paid)
        const cost =
          noBalance.cost !== undefined
            ? new Decimal(noBalance.cost)
            : avgPurchasePrice.mul(totalShares);
        // Value = current price × total shares
        const value = new Decimal(currentNoPrice).mul(totalShares);
        const pnl = value.minus(cost);
        const pnlPercent = cost.isZero()
          ? new Decimal(0)
          : value.dividedBy(cost).mul(100).minus(100);

        // Calculate TP/SL display data
        const noTpSlData = noTpSlOrders.data || [];
        let noTpSlDisplay = null;
        if (noTpSlData.length > 0) {
          if (noTpSlData.length === 1) {
            // Single order: show prices
            noTpSlDisplay = {
              ordersCount: 1,
              takeProfitPrice: noTpSlData[0].takeProfitPrice,
              stopLossPrice: noTpSlData[0].stopLossPrice,
            };
          } else {
            // Multiple orders: show total sizes
            noTpSlDisplay = {
              ordersCount: noTpSlData.length,
              totalTpSize: noTpSlData.reduce(
                (sum, order) => (order.takeProfitPrice > 0 ? sum + order.quantity : sum),
                0,
              ),
              totalSlSize: noTpSlData.reduce(
                (sum, order) => (order.stopLossPrice > 0 ? sum + order.quantity : sum),
                0,
              ),
            };
          }
        }

        positions.push({
          id: `no-${noTokenId}`,
          outcome: 'No',
          totalShares: totalShares,
          shares: noAvailableShares, // Show available shares (b - f)
          eventTokenId: noTokenId,
          openPrice: avgPurchasePrice.isZero() ? '0¢' : formatCents(avgPurchasePrice.mul(100)),
          tpSl: noTpSlDisplay,
          value: formatMoney(value.toNumber(), { prefix: '$' }),
          cost: formatMoney(cost.toNumber(), { prefix: '$' }),
          costValue: cost.toNumber(),
          openPriceCents: avgPurchasePrice.mul(100).toNumber(),
          pnl: {
            amount: formatMoney(pnl.toNumber(), { prefix: '$' }),
            percent: `${formatMoney(pnlPercent.toNumber(), { decimals: 2 })}%`,
          },
        });
      }
    }

    return positions;
  }, [
    balancesData,
    resolutions,
    yesTokenId,
    noTokenId,
    calculateAveragePurchasePrice,
    yesTpSlOrders.data,
    noTpSlOrders.data,
    currentYesPrice,
    currentNoPrice,
  ]);

  // Handler for Sell button click - fills order form with position data
  const handleSellClick = useCallback(
    (row: OpenPositionRow) => {
      // Set action to SELL
      setAction(EMarketAction.SELL);

      // Set intent based on outcome (Yes -> YES, No -> NO)
      setIntent(row.outcome === 'Yes' ? EMarketIntent.YES : EMarketIntent.NO);

      // Set order type to MARKET
      setOrderType(EMarketOrderType.MARKET);

      // Set shares count from position
      setSharesCount(new Decimal(row.shares));

      // Clear limitPrice and amountUsd (not needed for market orders)
      setLimitPrice(new Decimal(0));
      setAmountUsd(new Decimal(0));

      // Open mobile form if on mobile
      setIsMobileFormOpened(true);

      // Trigger lightning animation on OrderForm
      triggerLightningAnimation();
    },
    [
      setAction,
      setIntent,
      setOrderType,
      setSharesCount,
      setLimitPrice,
      setAmountUsd,
      setIsMobileFormOpened,
      triggerLightningAnimation,
    ],
  );

  // Handler for Share button click - opens share dialog
  const handleShareClick = useCallback((row: OpenPositionRow) => {
    setSharePosition(row);
    setOpenShareDialog(true);
  }, []);

  // Memoized share position data for the dialog
  const sharePositionData = useMemo(() => {
    if (!sharePosition || !event) return null;

    const myBet = sharePosition.costValue;
    const avgPriceCents = sharePosition.openPriceCents;

    // Possible win = totalShares × $1 (assuming each share pays $1 if correct)
    const possibleWin = sharePosition.totalShares;

    // Position wins when the resolution for the held side (Yes/No) is the winning one
    const isResolved = selectedOutcome?.status === 'RESOLVED';
    const sideResolution = selectedOutcome?.r?.find(
      (res) => res.isPositive === (sharePosition.outcome === 'Yes'),
    );

    const isWon = isResolved && sideResolution?.isWin === true;

    return {
      eventTitle: event.title,
      eventImage: event.image || '/evfallback.webp',
      outcome: sharePosition.outcome,
      outcomeName: selectedOutcome?.groupItemTitle,
      isResolved,
      isWon,
      entryPrice: avgPriceCents,
      // Resolved token settles at 100¢ (won) or 0¢ (lost); still open → no exit yet
      exitPrice: isResolved ? (isWon ? 100 : 0) : undefined,
      myBet,
      possibleWin,
    };
  }, [sharePosition, event, selectedOutcome]);

  const openTpSl = useCallback((row: OpenPositionRow) => {
    setSelectedPosition(row);
    setOpenTpSlDialog(true);
  }, []);

  // Calculate average price and current price for selected position
  const getPositionData = useCallback(
    (row: OpenPositionRow) => {
      // averageOpenPrice from the balance when it is known, else the row's own figure.
      const positionBalance = balancesData?.balances?.find(
        (b) => b.eventTokenId === row.eventTokenId,
      );
      const avgOpenPrice = positionBalance?.averageOpenPrice ?? row.openPriceCents / 100;

      // Get current price based on outcome
      const currentPrice = row.outcome === 'Yes' ? currentYesPrice : currentNoPrice;

      return {
        avgOpenPrice,
        currentPrice,
      };
    },
    [currentYesPrice, currentNoPrice, balancesData],
  );

  // Get totalVolume and availableShares for selected position from cache (updated via WebSocket)
  const selectedPositionTotalVolume = useMemo(() => {
    if (!selectedPosition || !balancesData?.balances) return 0;
    const positionBalance = balancesData.balances.find(
      (b) => b.eventTokenId === selectedPosition.eventTokenId,
    );
    return positionBalance?.totalVolume ?? 0;
  }, [selectedPosition, balancesData]);

  const selectedPositionAvailableShares = useMemo(() => {
    if (!selectedPosition || !balancesData?.balances) return 0;
    const positionBalance = balancesData.balances.find(
      (b) => b.eventTokenId === selectedPosition.eventTokenId,
    );
    return positionBalance?.value ?? 0; // value = available shares (b - f from WebSocket)
  }, [selectedPosition, balancesData]);

  // Clear selected position and close dialog if position no longer exists in rows
  useEffect(() => {
    if (selectedPosition) {
      const positionStillExists = rows.some((row) => row.id === selectedPosition.id);
      if (!positionStillExists) {
        setSelectedPosition(null);
        setOpenTpSlDialog(false);
      }
    }
  }, [rows, selectedPosition]);

  // Prevent dialog from opening automatically when new position appears
  // Only open dialog when user explicitly clicks "Add" or TP/SL link
  useEffect(() => {
    // If dialog is open but no position is selected, close it
    if (openTpSlDialog && !selectedPosition) {
      setOpenTpSlDialog(false);
    }
  }, [openTpSlDialog, selectedPosition]);

  const header = (
    <div className="flex min-h-6 w-full min-w-[800px] items-center gap-3 border-b border-v2-border pb-2">
      <span className="flex-1 min-w-0 text-[10px] leading-none text-v2-text-secondary">
        {t('outcome')}
      </span>
      <span className="w-[110px] text-[10px] leading-none text-v2-text-secondary">
        {t('totalAvailableShares')}
      </span>
      <span className="flex-[1.5] min-w-0 text-[10px] leading-none text-v2-text-secondary">
        {t('openPrice')}
      </span>
      <span className="w-[110px] text-[10px] leading-none text-v2-text-secondary">{t('tpSl')}</span>
      <span className="flex-1 min-w-0 text-[10px] leading-none text-v2-text-secondary">
        {t('value')}
      </span>
      <span className="flex-1 min-w-0 text-[10px] leading-none text-v2-text-secondary">
        {t('cost')}
      </span>
      <div className="flex shrink-0 items-center gap-8">
        <span className="w-[100px] text-right text-[10px] leading-none text-v2-text-secondary">
          {t('pnl')}
        </span>
        <div aria-hidden className="h-6 w-[101px] shrink-0" />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 overflow-x-auto px-3 pb-8">
        {header}
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex w-full min-w-[800px] items-center gap-3">
            <div className="flex-1">
              <Skeleton className="h-5 w-10 rounded-[8px] bg-v2-btn-secondary-2" />
            </div>
            <Skeleton className="h-4 w-[90px] bg-v2-btn-secondary-2" />
            <div className="flex-1">
              <Skeleton className="h-4 w-10 bg-v2-btn-secondary-2" />
            </div>
            <Skeleton className="h-4 w-[90px] bg-v2-btn-secondary-2" />
            <div className="flex-1">
              <Skeleton className="h-4 w-12 bg-v2-btn-secondary-2" />
            </div>
            <div className="flex-1">
              <Skeleton className="h-4 w-12 bg-v2-btn-secondary-2" />
            </div>
            <Skeleton className="h-4 w-[80px] bg-v2-btn-secondary-2" />
            <Skeleton className="h-10 w-[101px] rounded-[10px] bg-v2-btn-secondary-2" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex min-h-[160px] items-center justify-center px-3 pb-8">
        <InlineEmpty>{t('noOpenPositions')}</InlineEmpty>
      </div>
    );
  }

  return (
    <>
      <div className="scrollbar-hide flex max-h-[300px] flex-col gap-4 overflow-x-auto overflow-y-auto px-3 pb-8">
        {header}
        {rows.map((row) => {
          const isYes = row.outcome === 'Yes';
          return (
            <div key={row.id} className="flex w-full min-w-[800px] items-center gap-3">
              <div className="flex flex-1 min-w-0">
                <span
                  className={cn(
                    'flex h-5 w-10 items-center justify-center rounded-[8px] px-2 py-[2px]',
                    'text-[12px] leading-4 tracking-[-0.12px]',
                    isYes ? 'bg-v2-green-surface text-v2-green' : 'bg-v2-red-surface text-red-400',
                  )}
                >
                  {isYes ? tCommon('yes') : tCommon('no')}
                </span>
              </div>
              <span className="w-[110px] text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary">
                {formatMoney(row.totalShares, { decimals: 2 })} /{' '}
                {formatMoney(row.shares, { decimals: 2 })}
              </span>
              <span className="flex-[1.5] min-w-0 text-[13px] leading-[1.5] text-v2-text-primary">
                {row.openPrice}
              </span>
              <div className="w-[110px]">
                {row.tpSl ? (
                  <button
                    type="button"
                    onClick={() => openTpSl(row)}
                    className="flex cursor-pointer flex-col gap-1 text-left outline-none"
                  >
                    {row.tpSl.ordersCount === 1 ? (
                      <>
                        <span className="text-[13px] leading-[1.5]">
                          <span className="text-v2-text-secondary">TP: </span>
                          <span className="text-v2-text-primary underline decoration-dotted underline-offset-2">
                            {row.tpSl.takeProfitPrice
                              ? formatCents(row.tpSl.takeProfitPrice * 100)
                              : '-'}
                          </span>
                        </span>
                        <span className="text-[13px] leading-[1.5]">
                          <span className="text-v2-text-secondary">SL: </span>
                          <span className="text-v2-text-primary">
                            {row.tpSl.stopLossPrice
                              ? formatCents(row.tpSl.stopLossPrice * 100)
                              : '-'}
                          </span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-[13px] leading-[1.5]">
                          <span className="text-v2-text-secondary">TP: </span>
                          <span className="text-v2-text-primary underline decoration-dotted underline-offset-2">
                            {row.tpSl.totalTpSize
                              ? formatMoney(row.tpSl.totalTpSize, { decimals: 2 })
                              : '-'}
                          </span>
                        </span>
                        <span className="text-[13px] leading-[1.5]">
                          <span className="text-v2-text-secondary">SL: </span>
                          <span className="text-v2-text-primary underline decoration-dotted underline-offset-2">
                            {row.tpSl.totalSlSize
                              ? formatMoney(row.tpSl.totalSlSize, { decimals: 2 })
                              : '-'}
                          </span>
                        </span>
                      </>
                    )}
                  </button>
                ) : (
                  <Button appearance="outline" size="S" onClick={() => openTpSl(row)}>
                    {t('add')}
                  </Button>
                )}
              </div>
              <span className="flex-1 min-w-0 text-[13px] leading-[1.5] text-v2-text-primary">
                {row.value}
              </span>
              <span className="flex-1 min-w-0 text-[13px] leading-[1.5] text-v2-text-primary">
                {row.cost}
              </span>
              <div className="flex shrink-0 items-center gap-8">
                <PnlCell amount={row.pnl.amount} percent={row.pnl.percent} />
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    appearance="accent"
                    size="M"
                    className="font-medium"
                    onClick={() => handleSellClick(row)}
                  >
                    {t('sell')}
                  </Button>
                  <button
                    type="button"
                    onClick={() => handleShareClick(row)}
                    aria-label="Share"
                    className="flex size-8 cursor-pointer items-center justify-center rounded-[10px] text-v2-icon-primary outline-none transition-colors hover:bg-v2-btn-secondary hover:text-v2-icon-primary-hover"
                  >
                    <Icon name="Share2" size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {selectedPosition && event && (
        <DialogTakeProfitStopLoss
          source="event_page"
          open={openTpSlDialog}
          onOpenChange={setOpenTpSlDialog}
          assetId={selectedPosition.eventTokenId}
          totalShares={selectedPositionTotalVolume}
          _availableShares={selectedPositionAvailableShares}
          eventId={event.id}
          eventOutcomeId={outcomeId}
          baseAssetId={selectedPosition.eventTokenId}
          quoteAssetId={quoteAssetId}
          intent={selectedPosition.outcome === 'Yes' ? 'YES' : 'NO'}
          side="SELL"
          {...getPositionData(selectedPosition)}
        />
      )}
      {openShareDialog && sharePositionData && (
        <DialogSharePosition
          open={openShareDialog}
          onOpenChange={setOpenShareDialog}
          position={sharePositionData}
        />
      )}
    </>
  );
};
